import { TReportsRequestParams } from "domain/repositories/reports-repository";
import React, { useEffect, useState, useMemo, useContext } from "react";
import { IReportsFiltersRepository } from "../../../domain/repositories/reports-filters-repository";
import { SegmentContext } from "./SegmentProvider";
import moment from "moment";

export type TReportsFiltersProviderProps = {
  reportsFiltersDatasource: IReportsFiltersRepository;
  children: React.ReactNode;
};

export type TFilterSelectOption = {
  value: string | number | null;
  label: string;
};

export type TFiltersState = {
  portfolio?: TFilterSelectOption;
  campaign?: TFilterSelectOption;
  creative?: TFilterSelectOption;
  url?: TFilterSelectOption;
  ranges?: TFilterSelectOption;
  startDate?: string;
  endDate?: string;
  devices?: TFilterSelectOption;
};

export type TSelectOptions = {
  portfolio?: TFilterSelectOption[];
  campaign?: TFilterSelectOption[];
  creative?: TFilterSelectOption[];
  url?: TFilterSelectOption[];
  ranges?: TFilterSelectOption[];
  devices?: TFilterSelectOption[];
};

export type TInsightsParameters = {
  portfolio?: TFilterSelectOption;
  campaign?: TFilterSelectOption;
  creative?: TFilterSelectOption;
  url?: TFilterSelectOption;
};

export type TReportsFilterProviderState = {
  setComparisonEnabled: (enabled: boolean) => void;
  comparisonEnabled: boolean;
  setFiltersIndex: (index: number) => void;
  filtersIndex: number;
  setValue: (name: keyof TFiltersState, value: any) => void;
  resetField: (name: keyof TFiltersState) => void;
  resetFields: (names: (keyof TFiltersState)[]) => void;
  selectOptions: TSelectOptions;
  filterState: TFiltersState[];
  unsavedFilters: TFiltersState[];
  clearAllFilters: () => void;
  saveFilters: () => void;
  loadingOptions?: boolean;
  loadingRanges?: boolean;
  loadingDevices?: boolean;
  requestParams: TReportsRequestParams[];
  fetchInsightsParameters: (parameters?: TInsightsParameters) => void;
  defaultFiltersLoading?: boolean;
  comparisonParams: boolean;
};

export const ReportsFilterContext =
  React.createContext<TReportsFilterProviderState>({
    setComparisonEnabled: () => null,
    comparisonEnabled: false,
    setFiltersIndex: () => null,
    filtersIndex: 0,
    setValue: () => null,
    saveFilters: () => null,
    resetField: () => null,
    resetFields: () => null,
    clearAllFilters: () => null,
    selectOptions: {},
    filterState: [],
    unsavedFilters: [],
    requestParams: [],
    fetchInsightsParameters: () => null,
    comparisonParams: false,
  });

const filterDefaultValues = {
  ranges: {
    value: "4",
    label: "Last 30 days",
  },
  devices: {
    value: "2",
    label: "All devices",
  },
};

const filterDefaultValuesArray = [filterDefaultValues, filterDefaultValues];

export const notFoundText = "Not Found";

export const ReportsFilterProvider = ({
  reportsFiltersDatasource,
  children,
}: TReportsFiltersProviderProps) => {
  const [filtersIndex, setFiltersIndex] = useState<number>(0);
  const [comparisonEnabled, setComparisonEnabled] = useState<boolean>(false);
  const [comparisonParams, setComparisonParams] = useState<boolean>(false);
  const [filterState, setFilterState] = useState<TFiltersState[]>(
    filterDefaultValuesArray
  );
  const [selectOptions, setSelectOptions] = useState<TSelectOptions>({});
  const [unsavedFilters, setUnsavedFilters] = useState<TFiltersState[]>(
    filterDefaultValuesArray
  );
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [loadingRanges, setLoadingRanges] = useState<boolean>(false);
  const [loadingDevices, setLoadingDevices] = useState<boolean>(false);
  const [defaultFiltersLoading, setDefaultFiltersLoading] =
    useState<boolean>(true);

  const setValue = (
    name: keyof TFiltersState,
    value?:
      | {
          label: string;
          value: string | number | null;
        }
      | string
  ) => {
    setUnsavedFilters((old) =>
      old.map((item, i) =>
        i === filtersIndex ? { ...item, [name]: value } : item
      )
    );
  };

  const setSelectOption = (
    name: keyof TSelectOptions,
    options: TFilterSelectOption[]
  ) => {
    setSelectOptions((old) => ({ ...old, [name]: options }));
  };

  const resetField = (name: keyof TFiltersState) => setValue(name, undefined);
  const resetFields = (names: (keyof TFiltersState)[]) => {
    names.forEach((name) => setValue(name, undefined));
  };
  const clearAllFilters = () => setUnsavedFilters(filterDefaultValuesArray);
  const { segmentUpdateEvent } = useContext(SegmentContext);

  const saveFilters = () => {
    setFilterState(unsavedFilters);

    segmentUpdateRequestParams.map((event) => {
      segmentUpdateEvent(event);
    });

    if (defaultFiltersLoading) {
      setDefaultFiltersLoading(false);
    }

    setComparisonParams(comparisonEnabled);
  };

  const fetchInsightsParameters = (parameters?: TInsightsParameters) => {
    setLoadingOptions(true);
    reportsFiltersDatasource
      .getInsightsParameters({
        PortfolioId: parameters?.portfolio?.value,
        CampaignId: parameters?.campaign?.value,
        CreativeId: parameters?.creative?.value,
        UrlId: parameters?.url?.value,
      })
      .then(
        ({
          portfolios,
          campaigns,
          creatives,
          urls,
          portfolioId,
          campaignId,
          creativeId,
          urlId,
        }) => {
          setSelectOptions((prevOptions) => ({
            ...prevOptions,
            portfolio: [
              {
                value: null,
                label: "All Portfolios",
              },
              ...portfolios.map((option) => ({
                value: option.id,
                label: option.description,
              })),
            ],
            campaign: [
              {
                value: null,
                label: "All Campaigns",
              },
              ...campaigns.map((option) => ({
                value: option.id,
                label: option.description,
              })),
            ],
            creative: [
              {
                value: null,
                label: "All Creatives",
              },
              ...creatives.map((option) => ({
                value: option.id,
                label: option.description,
              })),
            ],
            url: [
              {
                value: null,
                label: "All URLs",
              },
              ...urls.map((option) => ({
                value: option.id,
                label: option.description,
              })),
            ],
          }));

          portfolioId || parameters?.portfolio?.value
            ? setValue("portfolio", {
                value: portfolioId || null,
                label:
                  portfolios.find((x) => x.id === portfolioId)?.description ||
                  notFoundText,
              })
            : setValue("portfolio", undefined);

          campaignId || parameters?.campaign?.value
            ? setValue("campaign", {
                value: campaignId || null,
                label:
                  campaigns.find((x) => x.id === campaignId)?.description ||
                  notFoundText,
              })
            : setValue("campaign", undefined);

          creativeId || parameters?.creative?.value
            ? setValue("creative", {
                value: creativeId || null,
                label:
                  creatives.find((x) => x.id === creativeId)?.description ||
                  notFoundText,
              })
            : setValue("creative", undefined);

          urlId || parameters?.url?.value
            ? setValue("url", {
                value: urlId || null,
                label:
                  urls.find((x) => x.id === urlId)?.description || notFoundText,
              })
            : setValue("url", undefined);
        }
      )
      .finally(() => setLoadingOptions(false));
  };

  const fetchRanges = () => {
    setLoadingRanges(true);
    reportsFiltersDatasource
      .getRanges()
      .then((ranges) =>
        setSelectOption(
          "ranges",
          ranges.map((range) => ({
            value: range.id,
            label: range.description,
          }))
        )
      )
      .finally(() => setLoadingRanges(false));
  };

  const fetchDevices = () => {
    setLoadingDevices(true);
    reportsFiltersDatasource
      .getDevices()
      .then((devices) =>
        setSelectOption(
          "devices",
          devices.map((device) => ({
            value: device.id,
            label: device.description,
          }))
        )
      )
      .finally(() => setLoadingDevices(false));
  };

  const getUrlFilterParams = () => {
    const urlParams = new URLSearchParams(window.location.search);

    const parseParam = (param: string) => {
      const baseParams = urlParams.getAll(param).map((value, index) => ({
        value,
        index,
      }));
      const numberedParams = urlParams
        .getAll(`${param}2`)
        .map((value) => ({ value, index: 1 }));

      const values = [...baseParams, ...numberedParams];
      return values.length > 0 ? values : null;
    };

    return {
      portfolioId: parseParam("portfolioId"),
      campaignId: parseParam("campaignId"),
      creativeId: parseParam("creativeId"),
      urlId: parseParam("urlId"),
      startDate: parseParam("startDate"),
      endDate: parseParam("endDate"),
      rangeId: parseParam("rangeId"),
      deviceId: parseParam("deviceId"),
      comparisonEnabled: urlParams.get("comparisonEnabled") === "true",
    };
  };

  const getUrlParamValue = (
    paramArray: { value: string; index: number }[] | null,
    filterIndex?: number
  ) =>
    paramArray?.find((param) => param.index == (filterIndex ?? filtersIndex))
      ?.value ?? null;

  const setUrlParametersValue = (urlParams: any, index: number) => {
    const startDate = getUrlParamValue(urlParams.startDate, index);
    const endDate = getUrlParamValue(urlParams.endDate, index);

    if (startDate && endDate) {
      setValue("ranges", undefined);

      setValue(
        "startDate",
        moment(startDate).format("MM/DD/yyyy") || undefined
      );
      setValue("endDate", moment(endDate).format("MM/DD/yyyy") || undefined);
    }

    fetchInsightsParameters({
      portfolio: {
        value: getUrlParamValue(urlParams.portfolioId, index),
        label: "",
      },
      campaign: {
        value: getUrlParamValue(urlParams.campaignId, index),
        label: "",
      },
      creative: {
        value: getUrlParamValue(urlParams.creativeId, index),
        label: "",
      },
      url: { value: getUrlParamValue(urlParams.urlId, index), label: "" },
    });
  };

  useEffect(() => {
    const urlParams = getUrlFilterParams();

    if (
      !urlParams.portfolioId &&
      !urlParams.campaignId &&
      !urlParams.creativeId &&
      !urlParams.urlId &&
      !urlParams.startDate &&
      !urlParams.endDate &&
      !urlParams.rangeId &&
      !urlParams.deviceId
    ) {
      setDefaultFiltersLoading(false);
    }

    fetchRanges();
    fetchDevices();

    setUrlParametersValue(urlParams, 0);
  }, []);

  useEffect(() => {
    const urlParams = getUrlFilterParams();
    const urlRangeParam = getUrlParamValue(urlParams.rangeId);

    if (
      defaultFiltersLoading &&
      !loadingRanges &&
      selectOptions.ranges?.length &&
      selectOptions.ranges?.length > 0 &&
      urlRangeParam
    ) {
      const urlRange = selectOptions.ranges?.find(
        (x) => x.value == urlRangeParam
      );

      if (urlRange) {
        setValue("ranges", urlRange);
      }
    }
  }, [loadingRanges, filtersIndex]);

  useEffect(() => {
    const urlParams = getUrlFilterParams();
    const urlDeviceParam = getUrlParamValue(urlParams.deviceId);

    if (
      defaultFiltersLoading &&
      !loadingDevices &&
      selectOptions.devices?.length &&
      selectOptions.devices?.length > 0 &&
      urlDeviceParam
    ) {
      const urlDevice = selectOptions.devices?.find(
        (x) => x.value == urlDeviceParam
      );

      if (urlDevice) {
        setValue("devices", urlDevice);
      }
    }
  }, [loadingDevices, filtersIndex]);

  const checkDefaultFiltersFilled = (): boolean => {
    const urlParams = getUrlFilterParams();

    const isPortfolioMissing =
      getUrlParamValue(urlParams.portfolioId) &&
      !unsavedFilters[filtersIndex].portfolio?.value;

    const isCampaignMissing =
      getUrlParamValue(urlParams.campaignId) &&
      !unsavedFilters[filtersIndex].campaign?.value;

    const isCreativeMissing =
      getUrlParamValue(urlParams.creativeId) &&
      !unsavedFilters[filtersIndex].creative?.value;

    const isUrlMissing =
      getUrlParamValue(urlParams.urlId) &&
      (!unsavedFilters[filtersIndex].creative?.value ||
        !unsavedFilters[filtersIndex].url?.value);

    const isStartDateMissing =
      getUrlParamValue(urlParams.startDate) &&
      !unsavedFilters[filtersIndex].startDate;

    const isEndDateMissing =
      getUrlParamValue(urlParams.endDate) &&
      !unsavedFilters[filtersIndex].endDate;

    const isRangeMissing =
      getUrlParamValue(urlParams.rangeId) &&
      getUrlParamValue(urlParams.rangeId) !=
        unsavedFilters[filtersIndex].ranges?.value;

    const isDeviceMissing =
      getUrlParamValue(urlParams.deviceId) &&
      getUrlParamValue(urlParams.deviceId) !=
        unsavedFilters[filtersIndex].devices?.value;

    return !(
      isPortfolioMissing ||
      isCampaignMissing ||
      isCreativeMissing ||
      isUrlMissing ||
      isStartDateMissing ||
      isEndDateMissing ||
      isRangeMissing ||
      isDeviceMissing
    );
  };

  useEffect(() => {
    const urlParams = getUrlFilterParams();

    if (defaultFiltersLoading && checkDefaultFiltersFilled()) {
      if (urlParams.comparisonEnabled && filtersIndex === 0) {
        setComparisonEnabled(true);
        setFiltersIndex(1);
      } else {
        saveFilters();
      }
    }
  }, [unsavedFilters]);

  useEffect(() => {
    const urlParams = getUrlFilterParams();

    if (
      defaultFiltersLoading &&
      urlParams.comparisonEnabled &&
      filtersIndex === 1
    ) {
      setUrlParametersValue(urlParams, 1);
    }
  }, [filtersIndex]);

  const requestParams = useMemo(
    () =>
      filterState.map((filter) => {
        return {
          RangeId: filter.ranges?.value,
          CampaignId: filter.campaign?.value,
          CreativeId: filter.creative?.value,
          PortfolioId: filter.portfolio?.value,
          UrlId: filter.url?.value,
          StartDate: filter.startDate,
          EndDate: filter.endDate,
          DeviceType: filter.devices?.value,
        };
      }),
    [filterState]
  );

  const segmentUpdateRequestParams = useMemo(
    () =>
      unsavedFilters.map((filter) => {
        return {
          filteredByAllPortfolios: !filter.portfolio?.value,
          filteredByAllCampaigns: !filter.campaign?.value,
          filteredByAllCreatives: !filter.creative?.value,
          filteredByAllUrls: !filter.url?.value,
          initialDate: filter.startDate,
          endDate: filter.endDate,
          rangeId: filter.ranges?.value,
          deviceId: filter.devices?.value,
        };
      }),
    [unsavedFilters]
  );

  return (
    <ReportsFilterContext.Provider
      value={{
        setComparisonEnabled,
        comparisonEnabled,
        setFiltersIndex,
        filtersIndex,
        setValue,
        resetField,
        resetFields,
        filterState,
        selectOptions,
        clearAllFilters,
        saveFilters,
        unsavedFilters,
        loadingOptions,
        loadingRanges,
        loadingDevices,
        requestParams,
        fetchInsightsParameters,
        defaultFiltersLoading,
        comparisonParams,
      }}
    >
      {children}
    </ReportsFilterContext.Provider>
  );
};
